import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Drawer,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import EditIcon from '@mui/icons-material/Edit';
import countriesData from '../../data/countries-states-cities-data';
import { countryFilterOptions, stateFilterOptions, cityFilterOptions } from '../../utils/locationSearch';
import {
  BOOKING_DRAWER_ACCENT,
  BOOKING_DRAWER_WIDTH_SX,
  createBookingDrawerOverlayProps,
  getBookingDrawerFooterSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerPaperSx,
  getBookingDrawerTheme,
  getGuestDrawerContentSx,
  getGuestDrawerFieldSx,
  getGuestDrawerSelectSx,
} from '../../utils/bookingDrawerLayout';

function buildFormFromCustomer(customer) {
  return {
    company_name: customer?.company_name || '',
    company_tax_id_type: customer?.company_tax_id_type || '',
    company_tax_id_number: customer?.company_tax_id_number || '',
    company_phone: customer?.company_phone || '',
    company_email: customer?.company_email || '',
    company_address: customer?.company_address || '',
    company_city: customer?.company_city || '',
    company_state: customer?.company_state || '',
    company_country: customer?.company_country || '',
    company_pincode: customer?.company_pincode || customer?.company_postal_code || '',
  };
}

/** Clear all company fields on a guest. */
export function clearCompanyDetailsApiPayload() {
  return {
    company_name: '',
    company_tax_id_type: '',
    company_tax_id_number: '',
    company_phone: '',
    company_email: '',
    company_address: '',
    company_city: '',
    company_state: '',
    company_country: '',
    company_pincode: '',
    company_postal_code: '',
  };
}


export default function CustomerCompanyDetailsDrawer({
  open,
  onClose,
  customer,
  initialMode = 'view',
  onSave,
  saving = false,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accent = BOOKING_DRAWER_ACCENT;
  const drawerTheme = getBookingDrawerTheme(isDark);
  const overlays = useMemo(
    () => createBookingDrawerOverlayProps(isDark, isDark ? drawerTheme.surface : '#ffffff'),
    [isDark, drawerTheme.surface]
  );

  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState(() => buildFormFromCustomer(customer));
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setMode(initialMode);
    setForm(buildFormFromCustomer(customer));
    setError('');
  }, [open, initialMode, customer]);

  const countries = Array.isArray(countriesData?.countries) ? countriesData.countries : [];
  const countryInput = (form.company_country || '').trim().toLowerCase();
  const selectedCountry =
    countries.find((c) => {
      const name = (c?.name || '').trim().toLowerCase();
      const code = (c?.code || '').trim().toLowerCase();
      return countryInput && (name === countryInput || code === countryInput);
    }) || null;
  const states = selectedCountry?.states || [];
  const stateInput = (form.company_state || '').trim().toLowerCase();
  const selectedState =
    states.find((s) => {
      const name = (s?.name || '').trim().toLowerCase();
      const code = (s?.code || '').trim().toLowerCase();
      return stateInput && (name === stateInput || code === stateInput);
    }) || null;
  const cities = selectedState?.cities || [];

  const fieldSx = useMemo(() => getGuestDrawerFieldSx(isDark), [isDark]);
  const selectSx = useMemo(() => getGuestDrawerSelectSx(isDark), [isDark]);
  const contentSx = useMemo(() => getGuestDrawerContentSx(isDark), [isDark]);

  const handleSave = async () => {
    setError('');
    const payload = {
      company_name: (form.company_name || '').trim() || null,
      company_tax_id_type: (form.company_tax_id_type || '').trim() || null,
      company_tax_id_number: (form.company_tax_id_number || '').trim() || null,
      company_phone: (form.company_phone || '').trim() || null,
      company_email: (form.company_email || '').trim() || null,
      company_address: (form.company_address || '').trim() || null,
      company_city: (form.company_city || '').trim() || null,
      company_state: (form.company_state || '').trim() || null,
      company_country: (form.company_country || '').trim() || null,
      company_pincode: (form.company_pincode || '').trim() || null,
      company_postal_code: (form.company_pincode || '').trim() || null,
    };
    try {
      await onSave?.(payload);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to update company details');
    }
  };

  const title =
    mode === 'edit'
      ? form.company_name || customer?.company_name
        ? 'Edit company details'
        : 'Add company details'
      : 'Company details';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={saving ? undefined : onClose}
      PaperProps={{
        sx: getBookingDrawerPaperSx(isDark, { width: BOOKING_DRAWER_WIDTH_SX }),
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1.5,
            ...getBookingDrawerHeaderSx(isDark),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon sx={{ color: accent, fontSize: 24 }} />
            <Box>
              <Typography
                variant="h6"
                sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 700, lineHeight: 1.2 }}
              >
                {title}
              </Typography>
              <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#64748b' }}>
                Billing and corporate information
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} disabled={saving} sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', p: 2, ...overlays.fieldShellSx, ...contentSx }}>
          {error ? (
            <Typography variant="body2" color="error" sx={{ mb: 1.5, fontWeight: 600 }}>
              {error}
            </Typography>
          ) : null}

          <Box sx={{ pointerEvents: mode === 'view' ? 'none' : 'auto', opacity: mode === 'view' ? 0.95 : 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company name"
                  value={form.company_name || ''}
                  onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company phone"
                  value={form.company_phone || ''}
                  onChange={(e) => setForm((f) => ({ ...f, company_phone: e.target.value }))}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Tax ID type"
                  value={form.company_tax_id_type || ''}
                  onChange={(e) => setForm((f) => ({ ...f, company_tax_id_type: e.target.value }))}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Tax ID number"
                  value={form.company_tax_id_number || ''}
                  onChange={(e) => setForm((f) => ({ ...f, company_tax_id_number: e.target.value }))}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company email"
                  type="email"
                  value={form.company_email || ''}
                  onChange={(e) => setForm((f) => ({ ...f, company_email: e.target.value }))}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={form.company_address || ''}
                  onChange={(e) => setForm((f) => ({ ...f, company_address: e.target.value }))}
                  multiline
                  rows={2}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={countries}
                  getOptionLabel={(option) => option?.name || ''}
                  filterOptions={countryFilterOptions}
                  value={
                    selectedCountry ||
                    (form.company_country
                      ? { name: form.company_country, code: form.company_country }
                      : null)
                  }
                  isOptionEqualToValue={(option, value) =>
                    (option?.name || '').toLowerCase() === (value?.name || '').toLowerCase() ||
                    (option?.code || '').toLowerCase() === (value?.code || '').toLowerCase()
                  }
                  onChange={(event, newValue) => {
                    setForm((f) => ({
                      ...f,
                      company_country: newValue?.name || '',
                      company_state: '',
                      company_city: '',
                    }));
                  }}
                  slotProps={overlays.autocompleteSlotProps}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth label="Country" sx={fieldSx} />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={states}
                  getOptionLabel={(option) => option?.name || ''}
                  filterOptions={stateFilterOptions}
                  value={
                    selectedState ||
                    (form.company_state
                      ? { name: form.company_state, code: form.company_state }
                      : null)
                  }
                  isOptionEqualToValue={(option, value) =>
                    (option?.name || '').toLowerCase() === (value?.name || '').toLowerCase() ||
                    (option?.code || '').toLowerCase() === (value?.code || '').toLowerCase()
                  }
                  onChange={(event, newValue) => {
                    setForm((f) => ({
                      ...f,
                      company_state: newValue?.name || '',
                      company_city: '',
                    }));
                  }}
                  disabled={!selectedCountry}
                  slotProps={overlays.autocompleteSlotProps}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth label="State" sx={fieldSx} />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={cities}
                  getOptionLabel={(option) => option || ''}
                  filterOptions={cityFilterOptions}
                  value={cities.find((city) => city === form.company_city) || null}
                  onChange={(event, newValue) =>
                    setForm((f) => ({ ...f, company_city: newValue || '' }))
                  }
                  disabled={!selectedState}
                  slotProps={overlays.autocompleteSlotProps}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth label="City" sx={fieldSx} />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Pincode / postal code"
                  value={form.company_pincode || ''}
                  onChange={(e) => setForm((f) => ({ ...f, company_pincode: e.target.value }))}
                  sx={fieldSx}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Box
          sx={{
            ...getBookingDrawerFooterSx(isDark),
            px: 1.75,
            py: 1.25,
            display: 'flex',
            gap: 1,
            width: '100%',
          }}
        >
          {mode === 'view' ? (
            <>
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  flex: 1,
                  borderRadius: 999,
                  py: 1.1,
                  fontWeight: 700,
                  borderColor: accent,
                  color: isDark ? accent : '#0f766e',
                }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => setMode('edit')}
                sx={{
                  flex: 1,
                  borderRadius: 999,
                  py: 1.1,
                  fontWeight: 700,
                  backgroundColor: accent,
                  color: '#042f2e',
                  '&:hover': { backgroundColor: accent, filter: 'brightness(0.97)' },
                }}
              >
                Edit
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  if (initialMode === 'edit') onClose();
                  else {
                    setMode('view');
                    setForm(buildFormFromCustomer(customer));
                    setError('');
                  }
                }}
                disabled={saving}
                sx={{
                  flex: 1,
                  borderRadius: 999,
                  py: 1.1,
                  fontWeight: 700,
                  borderColor: accent,
                  color: isDark ? accent : '#0f766e',
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{
                  flex: 1,
                  borderRadius: 999,
                  py: 1.1,
                  fontWeight: 700,
                  backgroundColor: accent,
                  color: '#042f2e',
                  '&:hover': { backgroundColor: accent, filter: 'brightness(0.97)' },
                }}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
